<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("payment_report.title") }}
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1">
                <template slot="header">
                    <div class="row align-items-center">
                        <div class="col-12">
                            <h3 class="mb-0">{{ $t("payment_report.title") }}</h3>
                        </div>
                    </div>
                </template>

                <!-- ============ Filters ============ -->
                <div class="px-4 pb-3">
                    <div class="row">
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("payment_report.filter_date_from") }}</label>
                            <el-date-picker
                                v-model="filters.date_from"
                                type="date"
                                value-format="yyyy-MM-dd"
                                :clearable="true"
                                style="width: 100%"
                            ></el-date-picker>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("payment_report.filter_date_to") }}</label>
                            <el-date-picker
                                v-model="filters.date_to"
                                type="date"
                                value-format="yyyy-MM-dd"
                                :clearable="true"
                                style="width: 100%"
                            ></el-date-picker>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("payment_report.filter_channel") }}</label>
                            <el-select v-model="filters.channel" clearable style="width: 100%">
                                <el-option :label="$t('payment_report.all')" value=""></el-option>
                                <el-option :label="$t('payment_report.channel_shop')" value="shop"></el-option>
                                <el-option :label="$t('payment_report.channel_member')" value="member"></el-option>
                                <el-option :label="$t('payment_report.channel_app')" value="app"></el-option>
                            </el-select>
                        </div>
                        <div class="col-md-3 mb-2">
                            <label class="form-control-label">{{ $t("payment_report.filter_status") }}</label>
                            <el-select v-model="filters.status" clearable style="width: 100%">
                                <el-option :label="$t('payment_report.all')" value=""></el-option>
                                <el-option :label="$t('payment_report.status_success')" value="1"></el-option>
                                <el-option :label="$t('payment_report.status_in_progress')" value="2"></el-option>
                                <el-option :label="$t('payment_report.status_failed')" value="3"></el-option>
                            </el-select>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12 text-right">
                            <base-button size="sm" type="primary" @click="search">
                                <i class="fa fa-search"></i>
                                {{ $t("payment_report.btn_search") }}
                            </base-button>
                            <base-button
                                size="sm"
                                type="success"
                                :disabled="exporting"
                                @click="exportCsv"
                            >
                                <i class="fa fa-download"></i>
                                {{ $t("payment_report.btn_export") }}
                            </base-button>
                        </div>
                    </div>
                </div>

                <!-- ============ Totals ============ -->
                <div class="px-4 pb-2">
                    <span class="badge badge-info mr-2">
                        {{ $t("payment_report.total_count") }}: {{ summary.count }}
                    </span>
                    <span class="badge badge-success">
                        {{ $t("payment_report.total_amount") }}: {{ formatAmount(summary.total_amount) }}
                    </span>
                </div>

                <!-- ============ Table ============ -->
                <div v-loading="loading">
                    <el-table
                        :data="rows"
                        row-key="id"
                        header-row-class-name="thead-light"
                        :empty-text="$t('payment_report.no_data')"
                    >
                        <el-table-column :label="$t('payment_report.col_invoice')" prop="invoice_no" min-width="160px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_channel')" prop="channel_name" min-width="130px"></el-table-column>
                        <!-- Owner 2026-10-26: how it was REALLY paid (Thai QR, TrueMoney, card
                             scheme, KBANK 123 …) — bo-api reads it from 2C2P's notice. -->
                        <el-table-column :label="$t('payment_report.col_pay_channel')" min-width="200px">
                            <template slot-scope="{ row }"><span data-testid="pr-pay-channel">{{ payChannelText(row) }}</span></template>
                        </el-table-column>
                        <!-- Owner 2026-10-28: 2C2P or 123 Service, and card / QR / wallet. -->
                        <el-table-column :label="$t('payment_report.col_provider')" min-width="190px">
                            <template slot-scope="{ row }"><span data-testid="pr-provider">{{ providerText(row) }}</span></template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_member')" min-width="110px">
                            <template slot-scope="{ row }">{{ row.member_id }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_recipient')" prop="recipient_name" min-width="140px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_amount')" min-width="110px" align="right">
                            <template slot-scope="{ row }">{{ formatAmount(row.amount) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_status')" min-width="120px">
                            <template slot-scope="{ row }">
                                <span class="badge" :class="statusClass(row.status)">{{ statusLabel(row.status) }}</span>
                            </template>
                        </el-table-column>
                        <!-- Owner 2026-10-28: paid + cancelled before the packing cut-off
                             = รอคืนเงิน until an admin records it on /seller/refunds. -->
                        <el-table-column :label="$t('payment_report.col_refund')" min-width="130px">
                            <template slot-scope="{ row }">
                                <span v-if="row.refund_status" class="badge" :class="row.refund_status === 'refunded' ? 'badge-success' : 'badge-warning'" data-testid="pr-refund">{{ $t("payment_report.refund_" + row.refund_status) }}</span>
                                <span v-else>-</span>
                            </template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_resp_code')" prop="resp_code" min-width="100px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_resp_desc')" prop="resp_desc" min-width="140px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_backend_resp_code')" prop="backend_resp_code" min-width="120px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_backend_resp_desc')" prop="backend_resp_desc" min-width="140px"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_tran_ref')" min-width="130px">
                            <template slot-scope="{ row }">{{ row.payment_tran_ref || "-" }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_attempt')" prop="attempt_no" min-width="90px" align="center"></el-table-column>
                        <el-table-column :label="$t('payment_report.col_req_time')" min-width="160px">
                            <template slot-scope="{ row }">{{ formatTime(row.req_time) }}</template>
                        </el-table-column>
                        <el-table-column :label="$t('payment_report.col_paid_time')" min-width="160px">
                            <template slot-scope="{ row }">{{ formatTime(row.payment_at) }}</template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- ============ Pagination (owner 2026-10-20: 10 a page) ============
                     Server-side: each page / size change asks the API for that page
                     only; `total` and the totals above cover the whole range. -->
                <div class="px-4 py-3 d-flex flex-wrap justify-content-end align-items-center" data-testid="payment-report-pagination">
                    <small v-if="total" class="text-muted mr-3" data-testid="payment-report-range">
                        {{ $t("payment_report.page_range", pageRange) }}
                    </small>
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next"
                        :total="total"
                        :page-sizes="pageSizes"
                        :page-size="pageSize"
                        :current-page="page"
                        :disabled="loading"
                        @current-change="onPageChange"
                        @size-change="onSizeChange"
                    ></el-pagination>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn, DatePicker, Select, Option, Pagination } from "element-ui";
import { SellerService } from "@/resource";
import { apiErrorMessage } from "@/util/api-error";
import swal from "sweetalert2";
import { PAYMENT_REPORT_DEFAULT_DAYS, defaultDateRange } from "@/utils/defaultDateRange";
import { sellerPaymentChannelLabel } from "@/utils/sellerOrderDetail";

// Server-side paging (owner 2026-10-20): GET /seller/payment-report takes
// page (1-based) + page_size (max 100) and answers `total` for the range.
const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

export default {
    name: "SellerPaymentReport",
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [DatePicker.name]: DatePicker,
        [Select.name]: Select,
        [Option.name]: Option,
        [Pagination.name]: Pagination
    },
    data() {
        return {
            loading: false,
            exporting: false,
            // Owner 2026-10-20 (spec awards-i18n P2): opens on the last 3 Bangkok
            // days. This page persists no filters (no URL / storage), so the
            // default always applies on load; a cleared date stays cleared.
            filters: Object.assign(
                {
                    date_from: "",
                    date_to: "",
                channel: "",
                    status: ""
                },
                defaultDateRange(PAYMENT_REPORT_DEFAULT_DAYS)
            ),
            rows: [],
            total: 0,
            // The filter set the current rows / totals were fetched with. Page
            // and size changes reuse it, so an edited-but-not-searched filter
            // never leaks into page 2 of an older search.
            appliedParams: {},
            requestSeq: 0,
            // page / page_size / filters of the rows on screen — restored when a
            // request fails, so the pager never points at a page it isn't showing.
            shown: { page: 1, pageSize: DEFAULT_PAGE_SIZE, params: {} },
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            pageSizes: PAGE_SIZES,
            summary: {
                count: 0,
                total_amount: 0
            }
        };
    },
    computed: {
        pageRange() {
            const total = this.total;
            const from = total ? (this.page - 1) * this.pageSize + 1 : 0;
            return { from, to: Math.min(total, from + this.rows.length - 1), total };
        }
    },
    mounted() {
        this.search();
    },
    methods: {
        payChannelText(row) {
            return sellerPaymentChannelLabel(row, this.$i18n && this.$i18n.locale) || "-";
        },
        // "2C2P · บัตรเครดิต/เดบิต", "123 Service · Internet / Mobile Banking" …
        providerText(row) {
            const parts = [];
            if (row.pay_provider === "2c2p" || row.pay_provider === "123") {
                parts.push(this.$t("payment_report.provider_" + row.pay_provider));
            }
            const group = row.payment_channel_group;
            if (["card", "qr", "wallet", "banking", "counter", "other"].indexOf(group) >= 0) {
                parts.push(this.$t("payment_report.group_" + group));
            }
            return parts.join(" · ") || "-";
        },
        queryParams() {
            const p = {};
            if (this.filters.date_from) p.date_from = this.filters.date_from;
            if (this.filters.date_to) p.date_to = this.filters.date_to;
            if (this.filters.channel) p.channel = this.filters.channel;
            if (this.filters.status) p.status = this.filters.status;
            return p;
        },
        onPageChange(page) {
            // el-pagination also emits current-change when it clamps the page
            // after a size change; the size handler already fetched page 1.
            if (page === this.page) return;
            this.page = page;
            this.fetchPage();
        },
        onSizeChange(size) {
            this.pageSize = size;
            this.page = 1;
            this.fetchPage();
        },
        // Every search is a new filter set: back to page 1.
        search() {
            this.appliedParams = this.queryParams();
            this.page = 1;
            return this.fetchPage();
        },
        async fetchPage() {
            const seq = ++this.requestSeq;
            this.loading = true;
            try {
                const params = Object.assign({}, this.appliedParams, {
                    page: this.page,
                    page_size: this.pageSize
                });
                const res = await SellerService.paymentReport(params);
                if (seq !== this.requestSeq) return; // a newer request superseded this one
                const data = (res.data && res.data.data) || {};
                const summary = data.summary || { count: 0, total_amount: 0 };
                const total = Number(data.total != null ? data.total : summary.count) || 0;
                const rows = data.rows || [];
                // The range shrank under us (e.g. rows changed between pages):
                // jump to the new last page instead of showing an empty table.
                const last = Math.max(1, Math.ceil(total / this.pageSize));
                if (!rows.length && total > 0 && this.page > last) {
                    this.page = last;
                    return this.fetchPage();
                }
                this.rows = rows;
                this.total = total;
                this.summary = summary;
                this.shown = { page: this.page, pageSize: this.pageSize, params: this.appliedParams };
            } catch (err) {
                if (seq !== this.requestSeq) return;
                // Put the pager back on the page still in the table.
                this.page = this.shown.page;
                this.pageSize = this.shown.pageSize;
                this.appliedParams = this.shown.params;
                swal.fire({
                    icon: "error",
                    title: this.$t("payment_report.load_error"),
                    text: apiErrorMessage(err)
                });
            } finally {
                if (seq === this.requestSeq) this.loading = false;
            }
        },
        async exportCsv() {
            this.exporting = true;
            try {
                const res = await SellerService.paymentReportExport(this.queryParams());
                const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "payment_report.csv";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            } catch (err) {
                swal.fire({
                    icon: "error",
                    title: this.$t("payment_report.load_error"),
                    text: apiErrorMessage(err)
                });
            } finally {
                this.exporting = false;
            }
        },
        statusLabel(status) {
            switch (status) {
                case 1:
                    return this.$t("payment_report.status_success");
                case 2:
                    return this.$t("payment_report.status_in_progress");
                case 3:
                    return this.$t("payment_report.status_failed");
                default:
                    return "-";
            }
        },
        statusClass(status) {
            switch (status) {
                case 1:
                    return "badge-success";
                case 2:
                    return "badge-warning";
                case 3:
                    return "badge-danger";
                default:
                    return "badge-secondary";
            }
        },
        formatAmount(value) {
            const n = Number(value || 0);
            return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        formatTime(value) {
            if (!value) return "";
            const d = new Date(value);
            if (isNaN(d.getTime())) return "";
            const pad = n => String(n).padStart(2, "0");
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
    }
};
</script>
